import { useEffect, useMemo, useRef, useState } from "react";
import { Icon, cx } from "../ui";
import type { StudioSegment } from "./studioPlan";

// §9.17/D139 — Timeline als ECHTE NLE-Spur (kein „Frame 5–8"-Chip mehr): jeder behaltene Clip ist
// ein Block, dessen BREITE proportional zur Dauer ist, mit Thumbnail (erstes Frame der Quelle) und
// Dauer-Badge (Sekunden). Ränder = Trim-Griffe (Zeit anpassbar, frame-genau); Body = Drag&Drop zum
// Umordnen; Playhead = senkrechte Linie über der Spur, Lineal oben zum Scrubben. K/Schere = Cut.
// Frame-Indizes erscheinen nur noch im Tooltip — nicht als sichtbares Label.

const TRACK_H = 60; // Spurhöhe (px)
const RULER_H = 18; // Lineal-Höhe (px)
const THUMB_MAX_H = 72; // Thumbnail-Auflösungsdeckel

/** Erstes Frame eines Clips als data-URL (gecovertes Spur-Thumbnail). Memo über die Frame-Identität. */
function useThumb(frame: ImageData | null | undefined): string | null {
  return useMemo(() => {
    if (!frame) return null;
    const scale = Math.min(1, THUMB_MAX_H / frame.height);
    const w = Math.max(1, Math.round(frame.width * scale));
    const h = Math.max(1, Math.round(frame.height * scale));
    const tmp = document.createElement("canvas");
    tmp.width = frame.width;
    tmp.height = frame.height;
    tmp.getContext("2d")?.putImageData(frame, 0, 0);
    const out = document.createElement("canvas");
    out.width = w;
    out.height = h;
    const ctx = out.getContext("2d");
    if (!ctx) return null;
    ctx.imageSmoothingEnabled = true;
    ctx.drawImage(tmp, 0, 0, w, h);
    return out.toDataURL();
  }, [frame]);
}

/** Dauer eines Clips menschenlesbar: „0,8 s" bzw. „1 Frame" für Mini-Clips. */
function durationLabel(len: number, fps: number): string {
  if (len <= 1) return "1 Frame";
  const secs = len / Math.max(1, fps);
  if (secs < 1) return `${secs.toFixed(2).replace(".", ",")} s`;
  return `${secs.toFixed(1).replace(".", ",")} s`;
}

type KeptClip = { seg: StudioSegment; index: number; len: number; start: number };

function ClipBlock({
  clip,
  fps,
  frame,
  active,
  selected,
  effectCount,
  dragging,
  onTrimStart,
  onSelect,
  onDelete,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDrop,
}: {
  clip: KeptClip;
  fps: number;
  frame: ImageData | null;
  active: boolean;
  selected: boolean;
  effectCount: number;
  dragging: boolean;
  onTrimStart: (edge: "l" | "r", e: React.PointerEvent) => void;
  onSelect: () => void;
  onDelete: () => void;
  onDragStart: (e: React.DragEvent) => void;
  onDragEnd: () => void;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
}) {
  const thumb = useThumb(frame);
  return (
    <div className="relative h-full w-full" onDragOver={onDragOver} onDrop={onDrop}>
      {/* Body = Drag-Quelle (umordnen) + Klick (Clip auswählen → Effekt-Panel + Playhead an Start).
          Thumbnail als Cover. `selected` = im Effekt-Panel bearbeitet (starker Ring); `active` =
          Playhead steht darüber (dezent). */}
      <div
        draggable
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        onClick={onSelect}
        className={cx(
          "absolute inset-0 cursor-grab overflow-hidden rounded-md border bg-surface-2 active:cursor-grabbing",
          selected
            ? "border-accent ring-2 ring-accent"
            : active
              ? "border-accent/70"
              : "border-border hover:border-accent/50",
          dragging && "opacity-40",
        )}
        style={
          thumb
            ? { backgroundImage: `url(${thumb})`, backgroundSize: "cover", backgroundPosition: "center" }
            : undefined
        }
        title={`Frames ${clip.seg.from}–${clip.seg.to - 1} · ${clip.len} Frames${effectCount > 0 ? ` · ${effectCount} Effekt(e)` : ""}`}
      >
        {/* Effekt-Anzahl-Badge (nur aktive Effekte) oben links. */}
        {effectCount > 0 && (
          <span className="pointer-events-none absolute left-0.5 top-0.5 rounded bg-accent/85 px-1 text-[9px] font-semibold text-white shadow">
            ✦ {effectCount}
          </span>
        )}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 flex items-center justify-between gap-1 bg-gradient-to-t from-black/70 to-transparent px-1.5 pb-0.5 pt-2">
          <span className="truncate text-[10px] font-medium tabular-nums text-white/90">
            {durationLabel(clip.len, fps)}
          </span>
        </div>
      </div>

      {/* Trim-Griffe: SIBLINGS des draggable Body (nicht Kinder) — so startet ihr Pointerdown kein
          HTML5-Drag des Clips (gleiche Falle wie Slider-im-draggable-Wrapper). */}
      <div
        onPointerDown={(e) => onTrimStart("l", e)}
        className="absolute inset-y-0 left-0 z-10 w-2 cursor-ew-resize rounded-l-md bg-accent/0 hover:bg-accent/60"
        title="Linken Rand trimmen"
      />
      <div
        onPointerDown={(e) => onTrimStart("r", e)}
        className="absolute inset-y-0 right-0 z-10 w-2 cursor-ew-resize rounded-r-md bg-accent/0 hover:bg-accent/60"
        title="Rechten Rand trimmen"
      />

      {/* Löschen (× oben rechts) — Sibling, kein Drag-Auslöser. */}
      <button
        type="button"
        onClick={onDelete}
        className="absolute right-0.5 top-0.5 z-20 grid h-4 w-4 place-items-center rounded bg-black/55 text-white/90 opacity-0 transition-opacity hover:bg-danger group-hover/clip:opacity-100"
        title="Clip entfernen"
      >
        <Icon name="trash" size={10} />
      </button>
    </div>
  );
}

export function Timeline({
  segments,
  totalFrames,
  outputCount,
  playhead,
  fps,
  frames,
  selectedId,
  onSelect,
  onScrub,
  onCut,
  onTrim,
  onReorder,
  onDelete,
  onRestore,
}: {
  segments: StudioSegment[];
  totalFrames: number;
  outputCount: number;
  playhead: number;
  fps: number;
  frames: ImageData[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  onScrub: (i: number) => void;
  onCut: () => void;
  onTrim: (id: string, from: number, to: number) => void;
  onReorder: (from: number, to: number) => void;
  onDelete: (id: string) => void;
  onRestore: (id: string) => void;
}) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  // Aktive Pointer-Geste (Scrub/Trim) → ihr window-Listener-Cleanup; bei Unmount mitten in einer
  // Geste sonst Leak (Codex-P2). Wird in beginScrub/startTrim gesetzt und im up-Handler geleert.
  const endGestureRef = useRef<(() => void) | null>(null);
  useEffect(() => () => endGestureRef.current?.(), []);

  // Behaltene Clips mit Output-Offset (Spur-Layout = Output-Reihenfolge, Breite ∝ Dauer) +
  // gelöschte Clips (für den Wiederherstellen-Streifen). index = Position im VOLLEN segments-Array
  // (das braucht onReorder/onTrim/onDelete).
  const { kept, deleted } = useMemo(() => {
    const k: KeptClip[] = [];
    const d: { seg: StudioSegment; index: number; len: number }[] = [];
    let start = 0;
    segments.forEach((seg, index) => {
      const len = Math.max(0, Math.min(seg.to, totalFrames) - seg.from);
      if (seg.keep && len > 0) {
        k.push({ seg, index, len, start });
        start += len;
      } else {
        d.push({ seg, index, len });
      }
    });
    return { kept: k, deleted: d };
  }, [segments, totalFrames]);

  const [scrubbing, setScrubbing] = useState(false);

  // Scrubben: Pointer-x → Output-Index (Frame-Mitte). down+move erlaubt Ziehen.
  function scrubAt(clientX: number) {
    const track = trackRef.current;
    if (!track || outputCount === 0) return;
    const rect = track.getBoundingClientRect();
    const f = (clientX - rect.left) / rect.width;
    onScrub(Math.max(0, Math.min(outputCount - 1, Math.round(f * outputCount - 0.5))));
  }
  // Drag-Scrub starten (Lineal ODER Playhead-Kopf): sofort setzen + bis pointerup verfolgen.
  function beginScrub(e: React.PointerEvent) {
    e.preventDefault();
    e.stopPropagation();
    if (outputCount === 0) return;
    setScrubbing(true);
    scrubAt(e.clientX);
    const move = (ev: PointerEvent) => scrubAt(ev.clientX);
    const up = () => {
      setScrubbing(false);
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      endGestureRef.current = null;
    };
    endGestureRef.current = up;
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  }

  // Trimmen: Pointer-Delta → Frame-Delta (pxProFrame am Gesten-Start fix). Live in den Store.
  function startTrim(clip: KeptClip, edge: "l" | "r", e: React.PointerEvent) {
    e.preventDefault();
    e.stopPropagation();
    const track = trackRef.current;
    if (!track || outputCount === 0) return;
    const pxPerFrame = track.getBoundingClientRect().width / outputCount;
    const startX = e.clientX;
    const { from, to, id } = clip.seg;
    const move = (ev: PointerEvent) => {
      const d = Math.round((ev.clientX - startX) / Math.max(0.0001, pxPerFrame));
      if (edge === "r") onTrim(id, from, to + d);
      else onTrim(id, from + d, to);
    };
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      endGestureRef.current = null;
    };
    endGestureRef.current = up;
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  }

  const playheadPct = outputCount > 0 ? ((playhead + 0.5) / outputCount) * 100 : 0;

  return (
    <div className="flex flex-col gap-1.5 border-t border-border bg-surface px-3 py-2">
      {/* Werkzeugleiste */}
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onCut}
          className="flex items-center gap-1 rounded-lg bg-surface-2 px-2 py-1 text-xs font-medium text-text hover:bg-accent/15 hover:text-accent disabled:opacity-40"
          title="Am Playhead schneiden (K)"
          disabled={outputCount === 0}
        >
          <Icon name="scissors" size={14} /> Schneiden
        </button>
        <span className="text-[11px] text-muted">
          {kept.length} {kept.length === 1 ? "Clip" : "Clips"} · {durationLabel(outputCount, fps)}
        </span>
        <span className="ml-auto text-[10px] text-muted">Rand ziehen = trimmen · Clip ziehen = umordnen</span>
      </div>

      {/* Spur: Lineal (scrubben) + Clips + Playhead-Linie */}
      <div ref={trackRef} className="relative select-none" style={{ height: RULER_H + TRACK_H }}>
        {/* Lineal (ganze Breite scrubbar) */}
        <div
          onPointerDown={beginScrub}
          className="absolute inset-x-0 top-0 cursor-ew-resize rounded bg-surface-2"
          style={{ height: RULER_H }}
        />

        {/* Clips — KEIN gap: Breite ∝ Dauer füllt die Spur lückenlos, damit Playhead-% und
            pxPerFrame (Breite/outputCount) frame-genau mit den Clip-Grenzen fluchten. */}
        <div className="absolute inset-x-0 bottom-0 flex" style={{ height: TRACK_H }}>
          {kept.length === 0 ? (
            <div className="grid h-full w-full place-items-center rounded-md border border-dashed border-border text-[11px] text-muted">
              Alle Clips entfernt — unten wiederherstellen.
            </div>
          ) : (
            kept.map((clip) => {
              const active = playhead >= clip.start && playhead < clip.start + clip.len;
              const effectCount = clip.seg.effects.filter((e) => e.enabled).length;
              return (
                <div
                  key={clip.seg.id}
                  className="group/clip relative h-full min-w-[24px]"
                  style={{ flexGrow: clip.len, flexBasis: 0 }}
                >
                  <ClipBlock
                    clip={clip}
                    fps={fps}
                    frame={frames[clip.seg.from] ?? null}
                    active={active}
                    selected={clip.seg.id === selectedId}
                    effectCount={effectCount}
                    dragging={dragIndex === clip.index}
                    onTrimStart={(edge, e) => startTrim(clip, edge, e)}
                    onSelect={() => {
                      onSelect(clip.seg.id); // Clip auswählen → Effekt-Panel bearbeitet ihn
                      onScrub(clip.start); // + Playhead an den Clip-Anfang
                    }}
                    onDelete={() => onDelete(clip.seg.id)}
                    onDragStart={(e) => {
                      e.dataTransfer.effectAllowed = "move";
                      e.dataTransfer.setData("text/plain", String(clip.index)); // Firefox: Drag nur mit Daten
                      setDragIndex(clip.index);
                    }}
                    onDragEnd={() => setDragIndex(null)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => {
                      e.preventDefault();
                      if (dragIndex !== null && dragIndex !== clip.index) onReorder(dragIndex, clip.index);
                      setDragIndex(null);
                    }}
                  />
                </div>
              );
            })
          )}
        </div>

        {/* Playhead über die ganze Spur — Linie ist klick-durchlässig, der KOPF ist greifbar/ziehbar. */}
        {outputCount > 0 && (
          <div
            className="pointer-events-none absolute top-0 z-30 w-px bg-accent"
            style={{ left: `${playheadPct}%`, height: RULER_H + TRACK_H }}
          >
            {/* Greifbarer Kopf (im Lineal-Band): anfassen und ziehen = scrubben. */}
            <div
              onPointerDown={beginScrub}
              className={cx(
                "pointer-events-auto absolute -top-1 left-1/2 -translate-x-1/2 cursor-ew-resize",
                scrubbing ? "scale-110" : "",
              )}
              style={{ width: 14, height: RULER_H + 4 }}
              title="Playhead ziehen"
            >
              <div className="mx-auto h-3 w-3 rounded-sm bg-accent shadow" />
              <div className="mx-auto -mt-0.5 h-0 w-0 border-x-[5px] border-t-[6px] border-x-transparent border-t-accent" />
            </div>
          </div>
        )}
      </div>

      {/* Wiederherstellen-Streifen (entfernte Clips, non-destruktiv) */}
      {deleted.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[10px] text-muted">Entfernt:</span>
          {deleted.map((d) => (
            <button
              key={d.seg.id}
              type="button"
              onClick={() => onRestore(d.seg.id)}
              className="flex items-center gap-1 rounded border border-border bg-surface-2 px-1.5 py-0.5 text-[10px] text-muted line-through hover:border-accent/50 hover:text-text"
              title={`Frames ${d.seg.from}–${Math.min(d.seg.to, totalFrames) - 1} wiederherstellen`}
            >
              <Icon name="plus" size={10} /> {durationLabel(d.len, fps)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
